import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Divider, Alert, Pagination, Card, CardContent,
  Switch, FormControlLabel, Avatar, InputAdornment, Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  LocalOffer as TagIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as UploadIcon,
  ContentCopy as CopyIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const statusColors = {
  active: 'success',
  inactive: 'default',
  out_of_stock: 'error',
  pending: 'warning',
  reserved: 'info',
  discontinued: 'error'
};

const AdminProducts = () => {
  const { t } = useTranslation('admin');

  const statusLabels = {
    active: t('products.status_active'),
    inactive: t('products.status_inactive'),
    out_of_stock: t('products.status_out_of_stock'),
    pending: t('products.status_pending'),
    reserved: t('products.status_reserved'),
    discontinued: t('products.status_discontinued')
  };

  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    out_of_stock: 0
  });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState({ open: false, url: '', name: '' });

  const emptyProduct = {
    name: '',
    description: '',
    original_price: '',
    sale_price: '',
    stock: '',
    category: '',
    image: '',
    status: 'active',
    specs: [] // 規格選項: [{ option_name, required, values: [{ name, price, prefix }] }]
  };

  // 圖片上傳參考
  const imageInputRef = React.useRef(null);

  // 圖片上傳處理
  const handleImageUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('products.select_image_file'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('products.image_too_large'));
      return;
    }

    // 上傳到服務器
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/upload/products', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // 使用服務器返回的 URL
        setEditProduct(prev => ({ ...prev, image: response.data.data.url }));
        toast.success(t('products.image_uploaded'));
      }
    } catch (error) {
      console.error('圖片上傳失敗:', error);
      toast.error(error.response?.data?.error || t('products.image_upload_failed'));
    }
  };

  // 新增規格選項
  const handleAddSpec = () => {
    setEditProduct(prev => ({
      ...prev,
      specs: [...(prev.specs || []), {
        option_name: '',
        required: false,
        values: [{ name: '', price: 0, prefix: '+' }]
      }]
    }));
  };

  // 移除規格選項
  const handleRemoveSpec = (specIndex) => {
    setEditProduct(prev => ({
      ...prev,
      specs: prev.specs.filter((_, i) => i !== specIndex)
    }));
  };

  // 更新規格名稱或必填狀態
  const handleUpdateSpec = (specIndex, field, value) => {
    setEditProduct(prev => ({
      ...prev,
      specs: prev.specs.map((spec, i) =>
        i === specIndex ? { ...spec, [field]: value } : spec
      )
    }));
  };

  // 新增規格值
  const handleAddSpecValue = (specIndex) => {
    setEditProduct(prev => ({
      ...prev,
      specs: prev.specs.map((spec, i) =>
        i === specIndex ? {
          ...spec,
          values: [...spec.values, { name: '', price: 0, prefix: '+' }]
        } : spec
      )
    }));
  };

  // 移除規格值
  const handleRemoveSpecValue = (specIndex, valueIndex) => {
    setEditProduct(prev => ({
      ...prev,
      specs: prev.specs.map((spec, i) =>
        i === specIndex ? {
          ...spec,
          values: spec.values.filter((_, vi) => vi !== valueIndex)
        } : spec
      )
    }));
  };

  // 更新規格值
  const handleUpdateSpecValue = (specIndex, valueIndex, field, value) => {
    setEditProduct(prev => ({
      ...prev,
      specs: prev.specs.map((spec, i) =>
        i === specIndex ? {
          ...spec,
          values: spec.values.map((v, vi) =>
            vi === valueIndex ? { ...v, [field]: value } : v
          )
        } : spec
      )
    }));
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [filters, pagination.page]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20,
        ...filters
      });
      const response = await api.get(`/admin/products?${params}`);
      if (response.data.success !== false) {
        const productList = response.data.products || response.data;
        const paginationData = response.data.pagination || {};
        setProducts(productList);
        setPagination(prev => ({
          ...prev,
          pages: paginationData.pages || paginationData.totalPages || 1,
          total: paginationData.total || productList.length
        }));
        // 使用分頁總數計算統計，而非當前頁面數量
        calculateStats(productList, paginationData.total || productList.length);
      }
    } catch (error) {
      // 認證失敗或 API 錯誤 → 顯示空清單而非假資料（避免誤導用戶以為自己有商品）
      setProducts([]);
      setPagination(prev => ({ ...prev, pages: 1, total: 0 }));
      calculateStats([], 0);
      if (error?.response?.status === 401) {
        // 401 通常代表 token 過期或無效 → 導回登入
        window.location.href = '/admin';
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // 後台使用 settings API 取得所有分類（含停用的）
      const response = await api.get('/settings/categories');
      if (response.data?.categories) {
        setCategories(response.data.categories);
      } else if (Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      // fallback: 嘗試前台 API
      try {
        const fallback = await api.get('/products/categories');
        if (fallback.data) setCategories(Array.isArray(fallback.data) ? fallback.data : []);
      } catch {
        setCategories([]);
      }
    }
  };

  const generateMockProducts = () => {
    return [
      { id: 1, name: '可愛動物造型筆', original_price: 49, sale_price: 29, stock: 150, category: '文具用品', status: 'active', image: '/images/pen.jpg' },
      { id: 2, name: '攜帶式噴霧瓶 100ml', original_price: 15, sale_price: 5, stock: 500, category: '生活雜貨', status: 'active', image: '/images/spray.jpg' },
      { id: 3, name: '環保購物袋', original_price: 199, sale_price: 99, stock: 80, category: '生活雜貨', status: 'active', image: '/images/bag.jpg' },
      { id: 4, name: '創意收納盒', original_price: 399, sale_price: 239, stock: 0, category: '居家收納', status: 'out_of_stock', image: '/images/box.jpg' },
      { id: 5, name: '手機支架', original_price: 299, sale_price: 149, stock: 200, category: '3C配件', status: 'active', image: '/images/stand.jpg' },
      { id: 6, name: '保濕面膜 10入', original_price: 299, sale_price: 99, stock: 50, category: '美妝保養', status: 'inactive', image: '/images/mask.jpg' },
      { id: 7, name: '彩色便利貼組', original_price: 89, sale_price: 39, stock: 300, category: '文具用品', status: 'active', image: '/images/notes.jpg' },
      { id: 8, name: 'USB充電線 Type-C', original_price: 199, sale_price: 79, stock: 180, category: '3C配件', status: 'active', image: '/images/cable.jpg' }
    ];
  };

  const calculateStats = (productList, totalCount = null) => {
    // 使用傳入的總數，若無則使用列表長度
    const total = totalCount || productList.length;
    // 當前頁面的統計
    const activeInPage = productList.filter(p => p.status === 'active').length;
    const inactiveInPage = productList.filter(p => p.status === 'inactive').length;
    const outOfStockInPage = productList.filter(p => p.stock === 0 || p.status === 'out_of_stock').length;

    // 由於 API 只返回 is_active=1 的商品，所有商品都是上架中
    const stats = {
      total: total,
      active: total, // 所有商品都是上架中 (is_active=1)
      inactive: 0,
      out_of_stock: outOfStockInPage // 當前頁面的售完數量
    };
    setStats(stats);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(products.map(p => p.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleAddProduct = () => {
    setEditProduct({ ...emptyProduct });
    setEditDialog(true);
  };

  const handleEditProduct = (product) => {
    setEditProduct({ ...product });
    setEditDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!editProduct.name || !editProduct.sale_price) {
      toast.error(t('products.name_price_required'));
      return;
    }

    try {
      if (editProduct.id) {
        await api.put(`/admin/products/${editProduct.id}`, editProduct);
        toast.success(t('products.updated'));
      } else {
        await api.post('/admin/products', editProduct);
        toast.success(t('products.added'));
      }
      setEditDialog(false);
      fetchProducts();
    } catch (error) {
      const msg = error?.response?.data?.error || (editProduct.id ? t('products.update_failed') : t('products.add_failed'));
      toast.error(msg);
    }
  };

  const handleDeleteConfirm = () => {
    if (selected.length === 0) {
      toast.warning(t('products.select_to_delete'));
      return;
    }
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete('/admin/products', { data: { ids: selected } });
      toast.success(t('products.deleted_count', { count: selected.length }));
      setSelected([]);
      fetchProducts();
    } catch (error) {
      const msg = error?.response?.data?.error || t('products.delete_failed');
      toast.error(msg);
    }
    setDeleteDialog(false);
  };

  const handleToggleStatus = async (product) => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/admin/products/${product.id}/status`, { status: newStatus });
      toast.success(newStatus === 'active' ? t('products.activated') : t('products.deactivated'));
      fetchProducts();
    } catch (error) {
      const msg = error?.response?.data?.error || t('products.update_failed');
      toast.error(msg);
    }
  };

  const handleBatchStatus = async (newStatus) => {
    if (selected.length === 0) {
      toast.warning(t('products.select_first'));
      return;
    }

    try {
      await api.put('/admin/products/batch/status', {
        ids: selected,
        status: newStatus
      });
      toast.success(t('products.batch_updated', { count: selected.length }));
      setSelected([]);
      fetchProducts();
    } catch (error) {
      const msg = error?.response?.data?.error || t('products.update_failed');
      toast.error(msg);
    }
  };

  const getDiscountPercent = (original, sale) => {
    if (!original || !sale) return 0;
    return Math.round((1 - sale / original) * 100);
  };

  const handleToggleFeatured = async (product) => {
    const newFeatured = !product.is_featured;
    try {
      await api.put(`/admin/products/${product.id}/featured`, { is_featured: newFeatured });
      toast.success(newFeatured ? t('products.set_featured') : t('products.unset_featured'));
      fetchProducts();
    } catch (error) {
      const msg = error?.response?.data?.error || t('products.update_failed');
      toast.error(msg);
    }
  };

  const handleBatchFeatured = async (isFeatured) => {
    if (selected.length === 0) {
      toast.warning(t('products.select_first'));
      return;
    }

    try {
      await api.put('/admin/products/batch/featured', {
        ids: selected,
        is_featured: isFeatured
      });
      toast.success(t('products.batch_featured_updated', { count: selected.length }));
      setSelected([]);
      fetchProducts();
    } catch (error) {
      const msg = error?.response?.data?.error || t('products.update_failed');
      toast.error(msg);
    }
  };

  return (
    <Box>
      {/* 頂部統計 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('products.all')}</Typography>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('products.status_active')}</Typography>
              <Typography variant="h4" color="success.main">{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('products.status_inactive')}</Typography>
              <Typography variant="h4" color="text.disabled">{stats.inactive}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('products.sold_out')}</Typography>
              <Typography variant="h4" color="error.main">{stats.out_of_stock}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 標題與操作 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('products.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddProduct}
          >
            {t('products.add')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProducts}
          >
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* 篩選 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t('products.search')}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                endAdornment: <SearchIcon color="action" />
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('products.product_status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('products.product_status')}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="active">{t('products.status_active')}</MenuItem>
                <MenuItem value="inactive">{t('products.status_inactive')}</MenuItem>
                <MenuItem value="out_of_stock">{t('products.status_out_of_stock')}</MenuItem>
                <MenuItem value="pending">{t('products.status_pending')}</MenuItem>
                <MenuItem value="reserved">{t('products.status_reserved')}</MenuItem>
                <MenuItem value="discontinued">{t('products.status_discontinued')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('products.category')}</InputLabel>
              <Select
                value={filters.category}
                label={t('products.category')}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<VisibilityIcon />}
                onClick={() => handleBatchStatus('active')}
                disabled={selected.length === 0}
                size="small"
              >
                {t('products.batch_activate')} ({selected.length})
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<VisibilityOffIcon />}
                onClick={() => handleBatchStatus('inactive')}
                disabled={selected.length === 0}
                size="small"
              >
                {t('products.batch_deactivate')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteConfirm}
                disabled={selected.length === 0}
                size="small"
              >
                {t('products.batch_delete')}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<StarIcon />}
                onClick={() => handleBatchFeatured(true)}
                disabled={selected.length === 0}
                size="small"
              >
                {t('products.set_featured')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<StarBorderIcon />}
                onClick={() => handleBatchFeatured(false)}
                disabled={selected.length === 0}
                size="small"
              >
                {t('products.unset_featured')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 商品列表 */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.length === products.length && products.length > 0}
                  indeterminate={selected.length > 0 && selected.length < products.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t('products.image')}</TableCell>
              <TableCell>{t('products.name')}</TableCell>
              <TableCell>{t('products.category')}</TableCell>
              <TableCell align="right">{t('products.original_price')}</TableCell>
              <TableCell align="right">{t('products.sale_price')}</TableCell>
              <TableCell align="right">{t('products.discount')}</TableCell>
              <TableCell align="right">{t('products.stock')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell align="center">{t('products.featured')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map(product => (
              <TableRow
                key={product.id}
                hover
                selected={selected.includes(product.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(product.id)}
                    onChange={() => handleSelect(product.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      position: 'relative',
                      cursor: product.image ? 'pointer' : 'default',
                      '&:hover .zoom-icon': { opacity: 1 }
                    }}
                    onClick={() => product.image && setImagePreview({ open: true, url: product.image, name: product.name })}
                  >
                    <Avatar
                      src={product.image}
                      variant="rounded"
                      sx={{ width: 50, height: 50, bgcolor: '#f5f5f5' }}
                    >
                      <InventoryIcon />
                    </Avatar>
                    {product.image && (
                      <Box
                        className="zoom-icon"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: 50,
                          height: 50,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s'
                        }}
                      >
                        <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {product.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {product.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={product.category} variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                    ${product.original_price}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="error">
                    ${product.sale_price}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    size="small"
                    label={`${getDiscountPercent(product.original_price, product.sale_price)}% OFF`}
                    color="error"
                    sx={{ fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography
                    fontWeight="bold"
                    color={product.stock === 0 ? 'error' : product.stock < 20 ? 'warning.main' : 'text.primary'}
                  >
                    {product.stock}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabels[product.status] || product.status}
                    color={statusColors[product.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={product.is_featured ? t('products.unset_featured') : t('products.set_featured')}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleFeatured(product)}
                      color={product.is_featured ? 'warning' : 'default'}
                    >
                      {product.is_featured ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t('common.edit')}>
                    <IconButton size="small" onClick={() => handleEditProduct(product)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={product.status === 'active' ? t('products.deactivate') : t('products.activate')}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleStatus(product)}
                      color={product.status === 'active' ? 'warning' : 'success'}
                    >
                      {product.status === 'active' ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? t('common.loading') : t('products.no_data')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分頁 */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={pagination.pages}
          page={pagination.page}
          onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
          color="primary"
        />
      </Box>

      {/* 編輯/新增對話框 */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editProduct?.id ? t('products.edit') : t('products.add')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* 商品圖片區塊 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('products.image')}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                {/* 圖片預覽 */}
                <Box
                  sx={{
                    width: 150,
                    height: 150,
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f9f9f9',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: editProduct?.image ? 'pointer' : 'default',
                    '&:hover .zoom-overlay': { opacity: 1 }
                  }}
                  onClick={() => editProduct?.image && setImagePreview({ open: true, url: editProduct.image, name: editProduct.name || '商品圖片' })}
                >
                  {editProduct?.image ? (
                    <>
                      <img
                        src={editProduct.image}
                        alt="商品圖片"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <Box
                        className="zoom-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s'
                        }}
                      >
                        <ZoomInIcon sx={{ color: 'white', fontSize: 32 }} />
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                      <ImageIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="caption" display="block">{t('products.no_image')}</Typography>
                    </Box>
                  )}
                </Box>
                {/* 上傳按鈕和 URL */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    建議尺寸: 800x800 px (JPG/PNG)，檔案大小不超過 2MB
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => imageInputRef.current?.click()}
                    sx={{ mb: 2 }}
                  >
                    {t('products.upload_image')}
                  </Button>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('products.image_url')}
                    value={editProduct?.image || ''}
                    onChange={(e) => setEditProduct(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                  />
                </Box>
              </Box>
            </Grid>

            {/* 基本資訊 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{t('products.basic_info')}</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('products.name')}
                value={editProduct?.name || ''}
                onChange={(e) => setEditProduct(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('products.description')}
                value={editProduct?.description || ''}
                onChange={(e) => setEditProduct(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>

            {/* 價格與庫存 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{t('products.price_stock')}</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label={t('products.original_price')}
                type="number"
                value={editProduct?.original_price || ''}
                onChange={(e) => setEditProduct(prev => ({ ...prev, original_price: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label={t('products.sale_price')}
                type="number"
                value={editProduct?.sale_price || ''}
                onChange={(e) => setEditProduct(prev => ({ ...prev, sale_price: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                required
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label={t('products.stock')}
                type="number"
                value={editProduct?.stock || ''}
                onChange={(e) => setEditProduct(prev => ({ ...prev, stock: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>{t('products.category')}</InputLabel>
                <Select
                  value={editProduct?.category_id || editProduct?.category || ''}
                  label={t('products.category')}
                  onChange={(e) => {
                    const selectedCat = categories.find(c => c.id === e.target.value || c.name === e.target.value);
                    setEditProduct(prev => ({
                      ...prev,
                      category_id: selectedCat?.id || e.target.value,
                      category: selectedCat?.name || e.target.value
                    }));
                  }}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>{t('common.status')}</InputLabel>
                <Select
                  value={editProduct?.status || 'active'}
                  label={t('common.status')}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="active">{t('products.status_active')}</MenuItem>
                  <MenuItem value="inactive">{t('products.status_inactive')}</MenuItem>
                  <MenuItem value="out_of_stock">{t('products.status_out_of_stock')}</MenuItem>
                  <MenuItem value="pending">{t('products.status_pending')}</MenuItem>
                  <MenuItem value="reserved">{t('products.status_reserved')}</MenuItem>
                  <MenuItem value="discontinued">{t('products.status_discontinued')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* 商品規格選項 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">{t('products.spec_options')}</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddSpec}>
                  {t('products.add_spec')}
                </Button>
              </Box>
              <Divider sx={{ mb: 2, mt: 1 }} />
            </Grid>
            {(editProduct?.specs || []).length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 1 }}>
                  {t('products.no_specs_hint')}
                </Alert>
              </Grid>
            ) : (
              (editProduct?.specs || []).map((spec, specIndex) => (
                <Grid item xs={12} key={specIndex}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                    {/* 規格名稱和必填選項 */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                      <TextField
                        size="small"
                        label={t('products.spec_name')}
                        value={spec.option_name}
                        onChange={(e) => handleUpdateSpec(specIndex, 'option_name', e.target.value)}
                        placeholder="例如：顏色、尺寸"
                        sx={{ width: 200 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={spec.required || false}
                            onChange={(e) => handleUpdateSpec(specIndex, 'required', e.target.checked)}
                          />
                        }
                        label={t('products.required')}
                      />
                      <Box sx={{ flex: 1 }} />
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddSpecValue(specIndex)}
                      >
                        {t('products.add_option')}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveSpec(specIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* 規格值列表 */}
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      {t('products.spec_values')}:
                    </Typography>
                    {spec.values.map((value, valueIndex) => (
                      <Box key={valueIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <TextField
                          size="small"
                          label={t('products.option_name')}
                          value={value.name}
                          onChange={(e) => handleUpdateSpecValue(specIndex, valueIndex, 'name', e.target.value)}
                          placeholder="例如：紅色、M號"
                          sx={{ width: 150 }}
                        />
                        <FormControl size="small" sx={{ width: 80 }}>
                          <InputLabel>{t('products.prefix')}</InputLabel>
                          <Select
                            value={value.prefix || '+'}
                            label={t('products.prefix')}
                            onChange={(e) => handleUpdateSpecValue(specIndex, valueIndex, 'prefix', e.target.value)}
                          >
                            <MenuItem value="+">{t('products.price_add')} +</MenuItem>
                            <MenuItem value="-">{t('products.price_subtract')} -</MenuItem>
                            <MenuItem value="=">{t('products.price_fixed')} =</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label={t('products.price')}
                          type="number"
                          value={value.price || 0}
                          onChange={(e) => handleUpdateSpecValue(specIndex, valueIndex, 'price', parseFloat(e.target.value) || 0)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                          sx={{ width: 120 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          {value.prefix === '+' && value.price > 0 && `(基礎價 + $${value.price})`}
                          {value.prefix === '-' && value.price > 0 && `(基礎價 - $${value.price})`}
                          {value.prefix === '=' && value.price > 0 && `(固定價 $${value.price})`}
                          {value.price === 0 && '(無加減價)'}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSpecValue(specIndex, valueIndex)}
                          disabled={spec.values.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>

          {editProduct?.original_price && editProduct?.sale_price && (
            <Alert severity="info" sx={{ mt: 2 }}>
              折扣幅度: {getDiscountPercent(editProduct.original_price, editProduct.sale_price)}% OFF
              （省 ${editProduct.original_price - editProduct.sale_price}）
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveProduct}>
            {editProduct?.id ? t('common.update') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認對話框 */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>{t('common.confirm_delete')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t('products.confirm_delete_msg', { count: selected.length })}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {t('common.confirm_delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 圖片預覽彈窗 */}
      <Dialog
        open={imagePreview.open}
        onClose={() => setImagePreview({ open: false, url: '', name: '' })}
        maxWidth="md"
        PaperProps={{
          sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setImagePreview({ open: false, url: '', name: '' })}
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Paper sx={{ p: 1, borderRadius: 2, overflow: 'hidden' }}>
            <img
              src={imagePreview.url}
              alt={imagePreview.name}
              style={{
                maxWidth: '80vw',
                maxHeight: '70vh',
                display: 'block',
                objectFit: 'contain'
              }}
            />
            {imagePreview.name && (
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', mt: 1, px: 2, pb: 1, color: 'text.secondary' }}
              >
                {imagePreview.name}
              </Typography>
            )}
          </Paper>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminProducts;
